import { useEffect } from "react";
import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

export default function WhatsappBusinessApiPage() {
  useEffect(() => {
    document.title = "WhatsApp Business API Services | Variable Soft";
    const description =
      "WhatsApp Business API setup, CRM integration, automated campaigns, chatbot flows and sales/support messaging workflows by Variable Soft.";
    let tag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.name = "description";
      document.head.appendChild(tag);
    }
    tag.content = description;
  }, []);

  const useCases = [
    "Lead capture and instant WhatsApp follow-up",
    "CRM-based sales and support conversations",
    "Template messages, reminders and notifications",
    "WhatsApp chatbot and AI response workflows",
    "Team inbox, routing and escalation setup",
    "Campaign tracking and conversation reporting",
  ];

  const deliverables = [
    "WhatsApp Business API onboarding support",
    "Template and message flow planning",
    "VSCRM or custom CRM integration",
    "Webhook, automation and reporting setup",
    "Agent handoff and escalation rules",
    "Testing, launch and ongoing support",
  ];

  return (
    <div className="min-h-screen bg-white text-slate-950">
      <Navigation />

      <section className="relative overflow-hidden bg-slate-950 pt-32 pb-20 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_15%_20%,rgba(34,197,94,0.22),transparent_55%),radial-gradient(760px_circle_at_90%_70%,rgba(34,211,238,0.18),transparent_55%)]" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65 }}
              className="lg:col-span-7"
            >
              <span className="inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-black uppercase tracking-widest text-emerald-200">
                WhatsApp Business API Services
              </span>
              <h1 className="mt-7 text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
                WhatsApp Business API setup, CRM integration and automation.
              </h1>
              <p className="mt-5 max-w-3xl text-lg leading-8 text-white/72">
                Connect WhatsApp with your sales, support and CRM workflows. Variable Soft helps businesses set up WhatsApp Business API, message templates, automation, chatbot flows, team routing and reporting.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a href="/getintouch" className="inline-flex items-center justify-center rounded-2xl bg-emerald-400 px-6 py-3 font-bold text-slate-950">
                  Discuss WhatsApp Setup
                </a>
                <a href="/services/whatsapp-ai-response" className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-6 py-3 font-semibold text-white">
                  Add AI Responses
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <div className="rounded-[30px] border border-white/10 bg-white/[0.06] p-6 shadow-2xl backdrop-blur-xl">
                <div className="grid h-16 w-16 place-items-center rounded-2xl bg-emerald-500 text-2xl text-white">
                  <i className="fab fa-whatsapp" />
                </div>
                <h2 className="mt-5 text-2xl font-extrabold">Useful for sales, support and service teams.</h2>
                <div className="mt-5 grid gap-3">
                  {["CRM connected conversations", "Automated template messages", "Bot + human handoff"].map((item) => (
                    <div key={item} className="rounded-2xl border border-white/10 bg-white/[0.05] p-4 text-sm font-semibold text-white/78">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-2">
            {[
              { title: "WhatsApp Use Cases", items: useCases },
              { title: "What We Deliver", items: deliverables },
            ].map((block) => (
              <div key={block.title} className="rounded-[28px] border border-slate-200 bg-slate-50 p-6">
                <h2 className="text-2xl font-extrabold text-slate-950">{block.title}</h2>
                <div className="mt-5 grid gap-3">
                  {block.items.map((item) => (
                    <div key={item} className="flex gap-3 rounded-2xl bg-white p-4 shadow-sm">
                      <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-emerald-500" />
                      <span className="text-sm font-semibold leading-relaxed text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_-50px_rgba(15,23,42,0.35)]">
            <div className="grid gap-5 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <h2 className="text-2xl font-extrabold text-slate-950">Already using VSCRM?</h2>
                <p className="mt-2 text-slate-600">
                  We can connect WhatsApp Business API with VSCRM workflows for lead assignment, follow-up reminders, chatbot routing and reporting.
                </p>
              </div>
              <a href="https://vscrm.in/integrations/whatsapp-business-api" target="_blank" rel="noreferrer" className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-6 py-3 font-bold text-white">
                View VSCRM WhatsApp API
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
